/**
 * Evidence shortcuts for the Work composer "/" picker — same saved + marketplace
 * list as the floating Ask Heidi / Chat protocols menu.
 */

import { getResolvedShortcutsForMarket } from '@/components/ui/floating-ask-heidi/shortcut-marketplace-data';
import {
  buildSearchableShortcuts,
  type SearchShortcut,
} from '@/components/ui/floating-ask-heidi/shortcut-search';
import { useIsAskHeidiSourcesShortcutsEnabled } from '@/features/flags';
import { IS_ASK_HEIDI_SHORTCUTS_ENABLED } from '@/features/flags/legacy-flags';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useEvidenceChipsConfig } from '@/hooks/useEvidenceChipsConfig';
import { composerLinkedItemsAtom } from '@heidi/agents/data';
import { Region } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export function useComposerEvidenceShortcuts(): {
  enabled: boolean;
  shortcuts: readonly SearchShortcut[];
} {
  const isGuidedShortcutsEnabled = useIsAskHeidiSourcesShortcutsEnabled();
  const shortcutsFeatureEnabled =
    IS_ASK_HEIDI_SHORTCUTS_ENABLED() || isGuidedShortcutsEnabled;

  const { pinnedChips } = useEvidenceChipsConfig();
  const linkedItems = useAtomValue(composerLinkedItemsAtom);
  const hasLinkedContext = linkedItems.length > 0;

  const { data: kindeProperties } = useQueryKindeProperties();
  const isUS = kindeProperties?.region === Region.US;
  const intl = useIntl();

  const marketShortcuts = useMemo(
    () => (shortcutsFeatureEnabled ? getResolvedShortcutsForMarket(isUS, intl) : []),
    [shortcutsFeatureEnabled, intl, isUS]
  );

  const visiblePinnedChips = useMemo(() => {
    if (!shortcutsFeatureEnabled) return [];
    // Match ChatInput: out-of-session with no linked patient/session surfaces
    // team shortcuts only — personal pins may assume consultation context.
    if (!hasLinkedContext) {
      return pinnedChips.filter((chip) => Boolean(chip.teamShortcutId));
    }
    return pinnedChips;
  }, [shortcutsFeatureEnabled, hasLinkedContext, pinnedChips]);

  const shortcuts = useMemo(
    () => buildSearchableShortcuts(visiblePinnedChips, marketShortcuts),
    [visiblePinnedChips, marketShortcuts]
  );

  return useMemo(
    () => ({
      enabled: shortcutsFeatureEnabled && shortcuts.length > 0,
      shortcuts,
    }),
    [shortcutsFeatureEnabled, shortcuts]
  );
}
